/**
 * product-api — 백엔드 /api/admin/products (hcltech-parts Product.Routes · docs/PRODUCT_SCREENS.md §1 · 2026-10-14).
 *   제품 목록(실무 제품만 — is_draft false 는 서버가 거른다) · 제품 상세(product 자체만 — 구성 줄까지. 실물은 재고 현황으로). 등록은 아직 시안(preview-product.ts).
 * 스키마는 백엔드 ProductView 와 같은 철자. 열거 셋은 값이 같아야 한다 — 다르면 Zod 가 경계에서 막는다.
 */
import { z } from 'zod';
import { getJson, postJson, putJson } from './http';
import { E_CATALOG_PENDING_STATUSES, E_CATALOG_TABLES, E_PART_NUMBER_COLUMNS } from './catalog-request-api';
import type { EPartNumberColumn } from './catalog-request-api';
import type { SlotFill } from './description-rule-api';

// ─── 열거 — 백엔드와 같은 철자 ───────────────────────────────────────
/** 카탈로그 연결 — 카탈로그에 있음 · 열린 요청 있음 · 둘 다 없음 */
export const E_CATALOG_LINKS = ['linked', 'requested', 'none'] as const;
export type  ECatalogLink    = typeof E_CATALOG_LINKS[number];

/** 목록 탭 — 전체 · 미연결(catalog <> linked) */
export const E_PRODUCT_TABS = ['all', 'unlinked'] as const;
export type  EProductTab    = typeof E_PRODUCT_TABS[number];

/** product 타입 */
export const E_PRODUCT_TYPES = ['part', 'server', 'set'] as const;
export type  EProductType    = typeof E_PRODUCT_TYPES[number];

/** 말 둘 — 목록 · 상세 · 등록이 같이 쓴다(preview-product 에서 이동, 2026-10-14) */

/** 제품의 모양 — 사람에게 보이는 분류 둘. 구성 줄 없으면 단품(섀시만 있는 서버도), 있으면 세트(백엔드 V장 "구성(= 세트)" ③, 2026-10-29) */
export const E_PRODUCT_SHAPES = ['single', 'set'] as const;
export type  EProductShape    = typeof E_PRODUCT_SHAPES[number];
export const PRODUCT_SHAPE_WORD: Record<EProductShape, string> = { single: '단품', set: '세트' };

/** 모양 판정 — 구성 줄 수(제품 상세 items.length · 실물 child_count)가 0 이면 단품 */
export function productShapeOf(itemCount: number): EProductShape {
  return itemCount === 0 ? 'single' : 'set';
}
export const CATALOG_LINK_WORD: Record<ECatalogLink, string> = { linked: '연결됨', requested: '요청 중', none: '없음' };

// ─── 스키마 ─────────────────────────────────────────────────────────
/** 목록 한 줄 — hclt_product 열 열둘 + 계산 열 셋(vendor · model 은 카탈로그에서, catalog 는 연결 판정). 재고 수는 없다 — 제품 상세의 것(2026-10-14) */
export const ProductViewSchema = z.object({
  id: z.string(),
  hclt_number: z.string(),
  type: z.enum(E_PRODUCT_TYPES),
  spec_category: z.string().nullable(),
  option_part: z.string().nullable(),
  description: z.string().nullable(),
  notes: z.string().nullable(),
  is_active: z.boolean(),                  // false = 단종 · 숨김(목록엔 안 온다)
  search_text: z.string().nullable(),      // 검색어 — 별칭 한 줄
  safety_stock: z.number().int().nullable(),   // 안전재고. null = 안 본다
  created_by: z.string().nullable(),
  created_at: z.string(),
  vendor: z.string().nullable(),
  model: z.string().nullable(),
  spare_part: z.string().nullable(),       // 카탈로그 행의 스페어 PN(343) — 파트넘버 밑 작은 줄(이카운트 비고 SPN #)
  assembly_part: z.string().nullable(),    // 카탈로그 행의 어셈블리 PN(343) — 스페어 옆 열(회의 2026-10-21 "번호 셋 다 보인다"). FIO 는 목록에서 뺐다(회의 2026-10-15)
  catalog_pending_id: z.number().int().nullable(),   // 'requested' 일 때 그 열린 요청(pending) id — 상세가 요청함 상세로 잇는다(2026-10-20). 아니면 null
  catalog: z.enum(E_CATALOG_LINKS),
});
export type ProductView = z.infer<typeof ProductViewSchema>;

export const ProductViewListSchema = z.object({
  rows: z.array(ProductViewSchema),
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int(),
});
export type ProductViewList = z.infer<typeof ProductViewListSchema>;

/** 구성 줄 하나(server · set) — hclt_product_item 열 넷 + PN 의 카탈로그 약식 설명(없으면 null) */
export const ProductItemViewSchema = z.object({
  id: z.string(),
  item_category: z.string(),
  option_part: z.string().nullable(),
  quantity: z.number().int(),
  description: z.string().nullable(),
  hclt_product_id: z.string().nullable(),   // 줄의 파트 product(346). 옛 줄은 null
  hclt_number: z.string().nullable(),       // 그 파트의 제품번호 — 상세 링크 글
});
export type ProductItemView = z.infer<typeof ProductItemViewSchema>;

/** 카탈로그 제원 한 칸 — 열 이름 · DDL 주석(라벨은 첫 문장) · 값(글) */
export const CatalogSpecViewSchema = z.object({
  name: z.string(),
  comment: z.string().nullable(),
  value: z.string(),
});
export type CatalogSpecView = z.infer<typeof CatalogSpecViewSchema>;

/** product 의 카탈로그 행 — part 는 PN 의 표 행, server 는 chassis SKU 행. 잠김(PN 넷 · 원문) · 바로(축약 · notes) · 제원(값 있는 열만) */
export const ProductCatalogViewSchema = z.object({
  table_name: z.enum(E_CATALOG_TABLES),
  option_part: z.string(),
  spare_part: z.string().nullable(),
  assembly_part: z.string().nullable(),
  factory_part: z.string().nullable(),
  vendor: z.string().nullable(),
  description: z.string().nullable(),
  description_raw: z.string().nullable(),
  notes: z.string().nullable(),
  specs: z.array(CatalogSpecViewSchema),
});
export type ProductCatalogView = z.infer<typeof ProductCatalogViewSchema>;

/** 히스토리 한 줄(2026-10-20) — 등록 · 승격 · 합침(받음) · 견적. 제품 칸 수정은 안 남는다(이벤트 표 없음). 견적만 href(그 딜) */
export const E_PRODUCT_HISTORY_KINDS = ['registered', 'promoted', 'merged_in', 'quoted'] as const;
export type EProductHistoryKind = typeof E_PRODUCT_HISTORY_KINDS[number];
export const ProductHistoryEntrySchema = z.object({
  at: z.string(),
  kind: z.enum(E_PRODUCT_HISTORY_KINDS),
  who: z.string().nullable(),
  text: z.string(),
  href: z.string().nullable(),
});
export type ProductHistoryEntry = z.infer<typeof ProductHistoryEntrySchema>;

/** 제품 상세 한 장 = 목록 한 줄 + 구성 줄(part 는 []) + 카탈로그 행(연결 안 됐으면 null) + 히스토리(최신순). 실물은 뷰가 따로 받는다(2026-10-14) */
export const ProductDetailSchema = ProductViewSchema.extend({
  items: z.array(ProductItemViewSchema),
  catalog_part: ProductCatalogViewSchema.nullable(),
  history: z.array(ProductHistoryEntrySchema),
});
export type ProductDetail = z.infer<typeof ProductDetailSchema>;

// ─── 제품 등록(문 ② 단독 등록 · 파트만 · 2026-10-14) ───
/** 카탈로그 PN 한 줄 — 찾기의 답. spec_category 는 서버가 표 이름으로 정한 것(모르는 표면 null) */
export const CatalogPartViewSchema = z.object({
  option_part: z.string(),
  vendor: z.string().nullable(),
  description: z.string().nullable(),
  spec_category: z.string().nullable(),
});
export type CatalogPartView = z.infer<typeof CatalogPartViewSchema>;

/** product 한 줄의 손잡이 — 같은 PN 의 product 가 이미 있을 때 */
export const ProductRefSchema = z.object({ id: z.string(), hclt_number: z.string() });
export type ProductRef = z.infer<typeof ProductRefSchema>;

/** 찾기의 열린 요청 — 친 번호가 확정 카탈로그엔 없고 요청함에 열려 있을 때(요청됨 · 확인 대기 · 보류). 등록 폼은 저장하면 그 요청에 잇는다(2026-11-04 — 10-30 막기를 뒤집음) */
export const OpenRequestRefSchema = z.object({ pending_id: z.number().int(), status: z.enum(E_CATALOG_PENDING_STATUSES) });
export type OpenRequestRef = z.infer<typeof OpenRequestRefSchema>;

/** 찾기 결과 — 둘 다 null 이면 "요청 한 건이 생깁니다". matched_column = 친 번호가 카탈로그 행의 어느 번호 칸에 맞았나(catalog 가 null 이면 null — 번호 한 칸, 2026-10-29) */
export const ProductLookupSchema = z.object({
  catalog: CatalogPartViewSchema.nullable(),
  matched_column: z.enum(E_PART_NUMBER_COLUMNS).nullable(),
  product: ProductRefSchema.nullable(),
  open_request: OpenRequestRefSchema.nullable(),   // 카탈로그에 있으면 늘 null
});
export type ProductLookup = z.infer<typeof ProductLookupSchema>;

/** 새 서버 찾기 — SKU 로 카탈로그 chassis 행(id · 기종 id) · 같은 SKU 의 서버 제품(있으면 막는다). 파트 찾기와 같은 꼴(2026-10-20) */
export const ServerLookupSchema = z.object({
  chassis: z.object({ id: z.number().int(), chassis_model_id: z.number().int().nullable() }).nullable(),
  product: ProductRefSchema.nullable(),
});
export type ServerLookup = z.infer<typeof ServerLookupSchema>;

// ─── 제품 합치기(반자동 · 2026-10-20) ───
/** 합칠 후보의 갈래 — 서버 E_MERGE_KINDS 와 같은 값. 같은 SKU 서버 · 스페어(어셈블리) 번호로 생긴 제품 */
export const E_MERGE_KINDS = ['same_sku', 'spare_number'] as const;
export type EMergeKind = typeof E_MERGE_KINDS[number];
export const MERGE_KIND_WORD: Record<EMergeKind, string> = { same_sku: '같은 파트넘버', spare_number: '파트넘버 · 스페어 등 혼용' };   // 'SKU' 는 화면 말이 아니다 · 혼용 = 한쪽이 스페어 · 어셈블리 번호로 등록됨(현민 2026-10-20)

/** 한쪽 제품 — 사람이 "같은 물건인가" 를 읽을 칸만 */
export const MergeSideSchema = z.object({
  id: z.string(),
  hclt_number: z.string(),
  option_part: z.string().nullable(),
  description: z.string().nullable(),
});
export type MergeSide = z.infer<typeof MergeSideSchema>;

/** 옮길 수 — 없어질 제품을 가리키던 것들 */
export const MergeCountSchema = z.object({
  units: z.number(),
  quote_items: z.number(),
  po_items: z.number(),
  ecount_links: z.number(),
  catalog_requests: z.number(),
});
export type MergeCount = z.infer<typeof MergeCountSchema>;

/** 확인창의 재료. blocked_reason 이 있으면 합칠 수 없다(버튼을 끈다) */
export const MergePreviewSchema = z.object({
  keep: MergeSideSchema,
  drop: MergeSideSchema,
  counts: MergeCountSchema,
  blocked_reason: z.string().nullable(),
});
export type MergePreview = z.infer<typeof MergePreviewSchema>;

/** 묶음 안 제품 한 줄 — 대표로 합칠 때의 옮길 수 · 막힌 이유. 대표 줄 자신은 counts 0 · null(백엔드 V장 "묶음 합치기", 2026-10-26) */
export const MergeGroupMemberSchema = MergeSideSchema.extend({ counts: MergeCountSchema, blocked_reason: z.string().nullable() });
export type MergeGroupMember = z.infer<typeof MergeGroupMemberSchema>;

/** 합칠 후보 한 줄 = 같은 물건 묶음 — keep_id 는 대표(서버 추천 · 미리보기면 사람이 고른 것). members 는 대표 먼저 → id 순 */
export const MergeGroupSchema = z.object({ kinds: z.array(z.enum(E_MERGE_KINDS)), keep_id: z.string(), members: z.array(MergeGroupMemberSchema) });
export type MergeGroup = z.infer<typeof MergeGroupSchema>;

/** 합치기 창에서 고를 수 있는 칸(백엔드 V장 "칸 고르기" 2026-10-26) — 검색어 · 메모는 늘, 디스크립션 · 카테고리는 대표가 카탈로그 미연결일 때만 */
export const E_MERGE_PICK_FIELDS = ['description', 'spec_category', 'search_text', 'notes'] as const;
export type EMergePickField = typeof E_MERGE_PICK_FIELDS[number];
/** 칸 하나를 어느 제품 것으로 남길지 — 서버가 그 제품의 지금 값을 읽어 대표에 쓴다 */
export type MergePick = { field: EMergePickField; product_id: string };

/** 등록 폼에 친 번호의 종류 — 서버 E_PART_NUMBER_KINDS 와 같은 값(카탈로그 열 이름). FIO 는 안 고른다(회의 2026-10-15 · 2026-10-19) */
export const E_PART_NUMBER_KINDS = ['option_part', 'spare_part', 'assembly_part'] as const;
export type EPartNumberKind = typeof E_PART_NUMBER_KINDS[number];
export const PART_NUMBER_KIND_WORD: Record<EPartNumberKind, string> = { option_part: 'Option Part', spare_part: 'Spare Part', assembly_part: 'Assembly Part' };   // 셀렉트 — 영어로(현민 2026-10-20 — 카탈로그 열 이름 그대로 읽힌다)
export const PART_NUMBER_KIND_SENTENCE_WORD: Record<EPartNumberKind, string> = { option_part: '옵션 파트', spare_part: '스페어 파트', assembly_part: '어셈블리' };   // 문장 안에선 한국말 — 한 문장에 영문 · 한글이 섞이지 않게(QA 2026-10-20 #23)
/** 찾기가 맞춘 번호 칸 넷의 문장 말 — 등록 폼은 번호 한 칸이라 종류를 안 고르고, 찾기가 어느 칸에 맞았는지 알려 준다(2026-10-29) */
export const PART_NUMBER_COLUMN_SENTENCE_WORD: Record<EPartNumberColumn, string> = { option_part: '옵션 파트', spare_part: '스페어 파트', assembly_part: '어셈블리', factory_part: '팩토리 파트' };

/** 단독 등록 입력(파트만) — 서버 PartProductInput 과 같은 키. 친 번호 한 칸(종류는 조사가 가른다 — 2026-10-29) · 제조사(미연결이면 서버가 specs.vendor 로). 빈 글 '' 은 서버가 null 로 읽는다. 안전재고는 폼에 없다(재고 쪽 — 2026-10-19) */
export type PartProductInput = { part_number: string; vendor: string; spec_category: string; description: string; specs: Record<string, string> | null; notes: string; search_text: string; description_rule_id: string | null; description_fills: SlotFill[] };   // description_rule_id null = 카테고리 기본 규칙 · description_fills = 채운 자리

/** 카탈로그 탭(pick 모드)이 등록 폼에 건네는 행 하나 — 표 · 행 id · 옵션 PN · 제조사 · 약식 설명 · 섀시면 기종 id(VOCABULARY "제품 등록 — 한 폼", 2026-10-22) */
export const CatalogPickSchema = z.object({
  table: z.string(),
  id: z.number().int(),
  option_part: z.string(),
  vendor: z.string().nullable(),
  description: z.string().nullable(),
  chassis_model_id: z.number().int().nullable(),
});
export type CatalogPick = z.infer<typeof CatalogPickSchema>;

/** 다시 찾기 — 등록 폼이 이미 열린 카탈로그 탭에 "이 글로 다시 찾아"(2026-10-26 — 주소를 바꾸면 탭이 통째로 다시 읽혀 앞 결과가 보였다) */
export const CatalogPickSearchSchema = z.object({ q: z.string() });
export type CatalogPickSearch = z.infer<typeof CatalogPickSearchSchema>;

/** 원하는 결과 없음 — 카탈로그 탭 → 폼. q = 그 탭의 찾기 글(2026-10-26 — 결과가 있어도 원하는 행이 없을 때) */
export const CatalogPickNoneSchema = z.object({ q: z.string() });
export type CatalogPickNone = z.infer<typeof CatalogPickNoneSchema>;

/** 파트넘버 열쇠 — 서버 PnKeyOf 와 같은 규칙(trim · 공백 하나 · 대문자). 친 글이 이와 다르면 저장 때 이렇게 바뀐다는 안내에 쓴다(QA 2026-10-20 #28) */
export function pnKeyOf(text: string): string {
  return text.trim().replace(/\s+/g, ' ').toUpperCase();
}

/** 목록 질의 — 탭 · 검색 · 필터 둘 + 페이지 시작(offset). '' = 전체(서버가 null 로 읽는다). 타입 필터는 없다 — 목록엔 단품만(2026-10-29 구성) */
export type ProductListQuery = { tab: EProductTab; q: string; category: string; vendor: string; offset: number };

// ─── 함수 — 백엔드 /api/admin/products ──────────────────────────────
const BASE = '/api/admin/products';
export const LIST_LIMIT = 500;   // 한 페이지 500 — 이전 · 다음으로 넘긴다(현민 2026-10-14. 실무 제품 1,254)

/** 파트넘버 자동완성 한 묶음 — 실무 제품(tab all)을 q 로 몇 줄만(파트넘버 · 품목 · 제품번호 · 검색어 ILIKE — 목록과 같은 조건). 재고 일별 탭이 쓴다(2026-10-21) */
export async function fetchProductMatches(q: string, limit: number): Promise<ProductView[]> {
  const params = new URLSearchParams({ tab: 'all', q, limit: String(limit), offset: '0' });
  const list = await getJson(`${BASE}?${params.toString()}`, { schema: ProductViewListSchema });
  return list.rows;
}

/** 제품 목록 한 묶음 — 서버가 거른다. 빈 칸은 안 보낸다 */
export async function fetchProducts(query: ProductListQuery): Promise<ProductViewList> {
  const params = new URLSearchParams({ tab: query.tab, limit: String(LIST_LIMIT), offset: String(query.offset) });
  if (query.q !== '') params.set('q', query.q);
  if (query.category !== '') params.set('category', query.category);
  if (query.vendor !== '') params.set('vendor', query.vendor);
  return getJson(`${BASE}?${params.toString()}`, { schema: ProductViewListSchema });
}

const ProductVendorListSchema = z.object({ rows: z.array(z.string()) });

/** 제조사 셀렉트의 선택지 — 실무 제품 전체의 distinct(서버 GET /vendors). 페이지 열 때 한 번. 카테고리는 SPEC_CATEGORIES 고정이라 안 받는다(QA 2026-10-20 #10) */
export async function fetchProductVendors(): Promise<string[]> {
  const list = await getJson(`${BASE}/vendors`, { schema: ProductVendorListSchema });
  return list.rows;
}

/** 제품 상세 한 장. 없는 id 는 서버 404 → getJson 이 던진다(상세가 "없는 제품" 으로 보인다) */
export async function fetchProduct(id: string): Promise<ProductDetail> {
  return getJson(`${BASE}/${encodeURIComponent(id)}`, { schema: ProductDetailSchema });
}

/** 찾기 — PN 치고 나갈 때. 종류 없이 카탈로그 번호 칸 넷을 본다(스페어로 맞으면 catalog 는 그 행 — 옵션 PN 이 실림 · matched_column = 맞은 칸, 2026-10-29). 서버가 열쇠 꼴(trim · 공백 하나 · 대문자)로 본다. 빈 pn 은 부르지 않는다(뷰가 막는다) */
/** GET /server-lookup?sku= — 새 서버 줄의 Option Part 를 치고 나갈 때. 빈 sku 는 부르지 않는다 */
export async function fetchServerLookup(sku: string): Promise<ServerLookup> {
  const params = new URLSearchParams({ sku });
  return getJson(`${BASE}/server-lookup?${params.toString()}`, { schema: ServerLookupSchema });
}

export async function fetchProductLookup(pn: string): Promise<ProductLookup> {
  const params = new URLSearchParams({ pn });
  return getJson(`${BASE}/lookup?${params.toString()}`, { schema: ProductLookupSchema });
}

/** 수정 입력 — 사람이 고치는 칸만(PN · 카탈로그 값은 절대 수정 안 됨, 현민 2026-10-14). 빈 글 '' 은 서버가 null 로. spec_category 는 파트만(서버 · 세트는 null) */
/** 미연결 파트의 specs 사람 칸 셋 — 제품 표엔 열이 없고 연결되면 카탈로그가 준다(2026-10-20). 빈 글은 서버가 키를 지운다 */
export type ProductSpecsText = { spare_part: string; assembly_part: string; vendor: string };
export type ProductUpdateInput = { description: string; notes: string; search_text: string; spec_category: string | null; is_active: boolean; specs_text: ProductSpecsText | null };   // 안전재고는 재고 › 실사 · 설정에서(2026-10-19). specs_text 는 미연결 파트만, 아니면 null

/** 수정 — 저장 뒤의 상세. 없으면 404 · 21종 밖 카테고리 400 */
export async function updateProduct(id: string, input: ProductUpdateInput): Promise<ProductDetail> {
  return putJson(`${BASE}/${encodeURIComponent(id)}`, input, ProductDetailSchema);
}

/** PN 정정 — 미연결 파트만(서버가 연결됨 · 같은 번호 제품 있음을 409 로 막는다). 번호 종류로 카탈로그를 찾아 연결(등록 폼과 같은 규칙, 2026-10-20). 바꾼 뒤의 상세(2026-10-15) */
export async function updateProductPartNumber(id: string, partNumber: string, kind: EPartNumberKind): Promise<ProductDetail> {
  return putJson(`${BASE}/${encodeURIComponent(id)}/part-number`, { part_number: partNumber, part_number_kind: kind }, ProductDetailSchema);
}

/** 메모만 — 목록의 메모 창(2026-10-19). 빈 글 '' 은 서버가 null 로(메모 지우기). 저장 뒤의 상세 */
export async function updateProductNotes(id: string, notes: string): Promise<ProductDetail> {
  return putJson(`${BASE}/${encodeURIComponent(id)}/notes`, { notes }, ProductDetailSchema);
}

/** 서버 · 세트 등록 입력(PRODUCT_SCREENS §5). 줄 = 파트 product(있으면 id, 없으면 PN · 설명 · 카테고리로 저장 때 생김) + 수량. server 는 섀시 하나(SKU 또는 기종 id), set 은 둘 다 null */
export type ServerItemInput = { hclt_product_id: string | null; option_part: string | null; description: string | null; spec_category: string; quantity: number };
export type ServerProductInput = {
  type: 'server' | 'set'; chassis_sku: string | null; chassis_model_id: number | null; chassis_text: string | null;
  description: string | null;
  description_rule_id: string | null;   // 고른 규칙. null = CHASSIS 기본 규칙(파트 입력과 같은 칸, 2026-10-29)
  description_fills: SlotFill[];        // 사람이 채운 자리
  items: ServerItemInput[]; notes: string | null; search_text: string | null; safety_stock: number | null;
};

/** 서버 · 세트 등록 — 만든(또는 견적 초안에서 올린) 뒤의 상세. 같은 구성이 실무 제품으로 이미 있으면 HTTP 409(메시지에 제품번호) */
export async function createServerProduct(input: ServerProductInput): Promise<ProductDetail> {
  return postJson(`${BASE}/server`, input, ProductDetailSchema);
}

/** 단독 등록 — 만든 뒤의 상세. 같은 PN 이면 HTTP 409 를 던진다(postJson) */
export async function createProduct(input: PartProductInput): Promise<ProductDetail> {
  return postJson(BASE, input, ProductDetailSchema);
}

/** 합칠 후보 — 같은 물건 묶음(짝을 이은 것). 막힌 줄도 온다(blocked_reason) */
export async function fetchMergeGroups(): Promise<MergeGroup[]> {
  const list = await getJson(`${BASE}/merge-groups`, { schema: z.object({ rows: z.array(MergeGroupSchema) }) });
  return list.rows;
}

/** 대표를 바꿨을 때 — 그 대표로 묶음을 다시 센다. memberIds 에 대표도 든다 */
export async function fetchMergeGroupPreview(keepId: string, memberIds: string[]): Promise<MergeGroup> {
  const params = new URLSearchParams({ keep_id: keepId, member_ids: memberIds.join(',') });
  return getJson(`${BASE}/merge-groups/preview?${params}`, { schema: MergeGroupSchema });
}

/** 묶음 합치기 — 한 트랜잭션. 남은 대표의 상세. 하나라도 막히면 HTTP 409(전부 되돌림) */
export async function mergeProductGroup(keepId: string, dropIds: string[], picks: readonly MergePick[]): Promise<ProductDetail> {
  const pickBody = picks.map((pick) => ({ field: pick.field, product_id: Number(pick.product_id) }));
  return postJson(`${BASE}/merge-groups`, { keep_id: Number(keepId), drop_ids: dropIds.map(Number), picks: pickBody }, ProductDetailSchema);
}

/** 합치기 확인창의 재료 — 두 제품 · 옮길 수 · 막힌 이유를 지금 값으로 */
export async function fetchMergePreview(keepId: string, dropId: string): Promise<MergePreview> {
  const params = new URLSearchParams({ drop_id: dropId });
  return getJson(`${BASE}/${encodeURIComponent(keepId)}/merge?${params}`, { schema: MergePreviewSchema });
}

/** 둘씩 합치기 — dropId 를 가리키던 것을 keepId 로 옮기고 dropId 를 닫는다. 남은 제품의 상세. 막히면 HTTP 409.
 *  bCloseDropRequests 면 없어질 제품이 혼자 건 열린 요청을 서버가 닫는다(PN 정정에서만 — 승격은 그 요청으로 다시 승격하므로 false, 2026-10-30) */
export async function mergeProducts(keepId: string, dropId: string, bCloseDropRequests: boolean): Promise<ProductDetail> {
  return postJson(
    `${BASE}/${encodeURIComponent(keepId)}/merge`,
    { drop_id: Number(dropId), close_drop_requests: bCloseDropRequests },
    ProductDetailSchema,
  );
}
